import React from 'react';
import { ScrollView, TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout';
import { SheetContent, SheetModal, SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { format } from 'date-fns';

import { STRINGS } from '@/constants/strings';
import type { PlayerProfileNoteData } from '@/features/player/components/types';

type PlayerProfileNoteDetailsSheetProps = {
  open: boolean;
  note: PlayerProfileNoteData | null;
  onOpenChange: (open: boolean) => void;
};

function formatNoteDate(date: Date): string {
  return format(date, 'EEEE, MMMM d, yyyy');
}

function parseNoteDate(rawDate?: string): Date | null {
  if (!rawDate) return null;
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

export function PlayerProfileNoteDetailsSheet({
  open,
  note,
  onOpenChange,
}: PlayerProfileNoteDetailsSheetProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  const closeSheet = () => onOpenChange(false);

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetContent
        tabletWidth={scale(375)}
        closeOnBackdropPress={false}
        enableDragToClose={false}
        style={isTablet ? styles.sheet : [styles.sheet, styles.sheetPhone]}>
        <Stack style={styles.sheetContainer}>
          <Stack direction="row" align="center" style={styles.sheetHeader}>
            <TouchableOpacity onPress={closeSheet} hitSlop={scale(12)} style={styles.backButton}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={24} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Regular 12px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.player.filters.back}
                </Text>
              </Stack>
            </TouchableOpacity>

            <Stack flex={1} align="center">
              <Text
                variant={
                  isTablet
                    ? 'Font Inter/Subtitle/Bold 14px'
                    : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
                }
                fontWeight={isTablet ? undefined : '800'}
                style={styles.title}>
                {STRINGS.player.profile.notesContent.detailsSheetTitle}
              </Text>
            </Stack>

            <Stack style={styles.headerRightSpacer} />
          </Stack>

          <Stack style={styles.divider} />

          <ScrollView
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
            bounces
            alwaysBounceVertical
            keyboardShouldPersistTaps="handled"
            style={styles.scrollView}
            contentContainerStyle={styles.bodyContent}>
            <Text
              variant={isTablet ? 'Font Inter/Subtitle/Bold 12px' : undefined}
              fontWeight={'700'}
              style={[styles.sectionTitle, isTablet ? styles.sectionTitleTablet : undefined]}>
              {note?.title ?? ''}
            </Text>

            <Text
              variant={isTablet ? 'Font Inter/Subtitle/Regular 14px' : undefined}
              style={[styles.noteDetail, !isTablet ? styles.noteDetailPhone : undefined]}>
              {note?.detail ?? ''}
            </Text>

            <Text
              variant={isTablet ? 'Font Inter/Subtitle/Regular 12px' : undefined}
              style={[styles.coachText, !isTablet ? styles.coachTextPhone : undefined]}>
              {note?.coachName ?? ''}
            </Text>

            <Text
              variant={isTablet ? 'Font Inter/Subtitle/Regular 12px' : undefined}
              style={[styles.dateText, !isTablet ? styles.coachTextPhone : undefined]}>
              {note ? formatNoteDate(parseNoteDate(note.rawDate) ?? note.createdAt) : ''}
            </Text>
          </ScrollView>
        </Stack>
      </SheetContent>
    </SheetModal>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  sheet: {
    backgroundColor: 'white',
  },
  scrollView: {
    flex: 1,
    backgroundColor: !isTablet ? 'white' : colors['1'],
  },
  sheetPhone: {
    height: '92%',
  },
  sheetContainer: {
    flex: 1,
  },
  sheetHeader: {
    paddingHorizontal: scale(20),
    paddingTop: scale(35),
    paddingBottom: scale(16),
  },
  backButton: {
    width: scale(45),
    alignItems: 'center',
    justifyContent: 'center',
  },
  backText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  title: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  headerRightSpacer: {
    width: scale(45),
  },
  divider: {
    height: 1,
    backgroundColor: colors['3'],
  },
  bodyContent: {
    flexGrow: 1,
    minHeight: '100%',
    paddingHorizontal: scale(16),
    paddingTop: scale(28),
    paddingBottom: scale(100),
  },
  sectionTitle: {
    color: colors.Blue,
    textTransform: 'uppercase',
    marginBottom: scale(16),
  },
  sectionTitleTablet: {
    fontSize: scale(11),
    fontWeight: '700',
  },
  noteDetail: {
    color: colors['10'],
    marginBottom: scale(28),
  },
  noteDetailPhone: {
    fontSize: scale(10),
    fontWeight: '400',
    color: colors['6'],
  },
  coachText: {
    color: colors['6'],
    marginBottom: scale(6),
  },
  coachTextPhone: {
    fontSize: scale(11),
    fontWeight: '400',
  },
  dateText: {
    color: colors['6'],
  },
}));
